import { lazy, Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import HomeSkeleton from "../components/common/Skeletons/HomeSkeleton";
import DataTableSkeleton from "../components/common/Skeletons/DataTableSkeleton";
import MFAListSkeleton from "../components/common/Skeletons/MFAListSkeleton";
import EPMDashboardSkeleton from "../components/common/Skeletons/EPMDashboardSkeleton";
import CloudConfiguration from "../components/dashboard/CloudDiscovery/CloudConfiguration";
import CloudDiscoveryOperations from "../components/dashboard/CloudDiscovery/CloudDiscoveryOperations/CloudDiscoveryOperations";
import SelectPlatform from "../components/dashboard/CloudDiscovery/SelectPlatform/SelectPlatform";
import ScanResults from "../components/dashboard/CloudDiscovery/ScanResults/ScanResults";
import ScanHistory from "../components/dashboard/CloudDiscovery/ScanHistory/ScanHistory";
import HardcodedCredentials from "../components/dashboard/HardcodedCredentials/index";
import CredFindings from "../components/dashboard/HardcodedCredentials/Findings/Findings";
import CredScanHistory from "../components/dashboard/HardcodedCredentials/ScanHistory/ScanHistory";
import CredScanDetailPage from "../components/dashboard/HardcodedCredentials/ScanHistory/ScanDetailPage";
import CredScanConfigs from "../components/dashboard/HardcodedCredentials/ScanConfigurations/ScanConfigurations";
import CredCreateEditConfigPage from "../components/dashboard/HardcodedCredentials/ScanConfigurations/CreateEditConfigPage";
import CredGitRepos from "../components/dashboard/HardcodedCredentials/GitConnections/GitConnections";
import CredGitConnectionFormPage from "../components/dashboard/HardcodedCredentials/GitConnections/GitConnectionFormPage";
const Logo = lazy(() => import("../components/Branding/logo"));
const DiscoveryTabs = lazy(() =>
  import("../components/dashboard/AccountsDiscovery/DiscoveryTabs"),
);
const ChangePassword = lazy(() =>
  import("../components/dashboard/AdminPassword/ChangePassword"),
);
const AddApp = lazy(() => import("../components/dashboard/Apps/AddApp/AddApp"));
const AllocateAppToUser = lazy(() =>
  import("../components/dashboard/Apps/AllocateApps/AllocateAppToUser"),
);
const MainAllocateApps = lazy(() =>
  import("../components/dashboard/Apps/AllocateApps/MainAllocateApps"),
);
const MainDeAllocateApps = lazy(() =>
  import("../components/dashboard/Apps/AllocateApps/MainDeAllocateApps"),
);
const EditMyApp = lazy(() =>
  import("../components/dashboard/Apps/EditMyApp/EditMyApp"),
);
const AddAsset = lazy(() => import("../components/dashboard/Assets/AddAsset/AddAsset"));
const AddApplicationCredentials = lazy(() => import("../components/dashboard/ApplicationCredentials/AddApplicationCredentials/AddApplicationCredentials"));
const MainAllocateApplicationCredentials = lazy(() =>
  import("../components/dashboard/ApplicationCredentials/AllocateApplicationCredentials/MainAllocateApplicationCredentials"),
);
const MainDeAllocateApplicationCredentials = lazy(() =>
  import("../components/dashboard/ApplicationCredentials/AllocateApplicationCredentials/MainDeAllocateApplicationCredentials"),
);
const AllocateAssetToUsers = lazy(() =>
  import("../components/dashboard/Assets/AllocateAssets/Allocateusers"),
);
const MainAllocateAsset = lazy(() =>
  import("../components/dashboard/Assets/AllocateAssets/MainAllocateAsset"),
);
const MainDeAllocateAsset = lazy(() =>
  import("../components/dashboard/Assets/AllocateAssets/MainDeAllocateAsset"),
);
const DeAllocateAssetFromUsers = lazy(() =>
  import("../components/dashboard/Assets/AllocateAssets/viewusers"),
);
const EditMyAsset = lazy(() =>
  import("../components/dashboard/Assets/EditMyAsset/EditMyAsset"),
);
const ConfigureSMTP = lazy(() =>
  import("../components/dashboard/ConfigureSMTP/ConfigureSMTP"),
);
const Home = lazy(() => import("../components/dashboard/Dashboard/Home"));
const AddGroup = lazy(() => import("../components/dashboard/Groups/AddGroup/AddGroup"));
const AssignUsers = lazy(() =>
  import("../components/dashboard/Groups/AssignUsers/AssignUsers"),
);
const AssignedUsers = lazy(() =>
  import("../components/dashboard/Groups/AssignedUser/AssignedUser"),
);
const AutoAssign = lazy(() =>
  import("../components/dashboard/Groups/AutoAssign/AutoAssign"),
);
const EditGroup = lazy(() =>
  import("../components/dashboard/Groups/EditGroup/EditGroup"),
);
const GroupsList = lazy(() =>
  import("../components/dashboard/Groups/GroupsList/GroupsList"),
);
const AddConfiguration = lazy(() =>
  import("../components/dashboard/NetworkAnalyzer/AddConfiguration/AddConfiguration"),
);
const Configurations = lazy(() =>
  import("../components/dashboard/NetworkAnalyzer/Configurations"),
);
const EditConfiguration = lazy(() =>
  import("../components/dashboard/NetworkAnalyzer/EditConfiguration/EditConfiguration"),
);
const ScannedHistory = lazy(() =>
  import("../components/dashboard/NetworkAnalyzer/ScannedHistory"),
);
const ScannedSystemUsers = lazy(() =>
  import("../components/dashboard/NetworkAnalyzer/ScannedSystemUsers"),
);
const NetworkConfiguration = lazy(() =>
  import("../components/dashboard/NetworkScanner/NetworkConfiguration"),
);
const NetworkConfigurationOperations = lazy(() =>
  import(
    "../components/dashboard/NetworkScanner/NetworkConfigurationOperations/NetworkConfigurationOperations"
  ),
);
const NetworkScanHistory = lazy(() =>
  import("../components/dashboard/NetworkScanner/NetworkScanHistory"),
);
const AddDatabasePolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/DatabasePolicy/AddDatabasePolicy/AddDatabasePolicy"
  ),
);
const DatabasePolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/DatabasePolicy/DatabasePolicyList/DatabasePolicy"
  ),
);
const EditDatabasePolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/DatabasePolicy/EditDatabasePolicy/EditDatabasePolicy"
  ),
);
const AddWebAppsPolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/WebAppsPolicy/AddWebAppsPolicy/AddWebAppsPolicy"
  ),
);
const EditWebAppPolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/WebAppsPolicy/EditWebAppsPolicy/EditWebAppsPolicy"
  ),
);
const AddRDPPolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/RdpPolicy/AddRdpPolicy/AddRdpPolicy"
  ),
);
const EditRDPPolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/RdpPolicy/EditRdpPolicy/EditRdpPolicy"
  ),
);
const AddSshPolicyPage = lazy(() =>
  import(
    "../components/dashboard/Policy/SshPolicy/AddSshPolicy/AddSshPolicy"
  ),
);
const EditSshPolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/SshPolicy/EditSshPolicy/EditSshPolicy"
  ),
);
const FileTransferControlPage = lazy(() =>
  import(
    "../components/dashboard/Policy/SshPolicy/FileTransferControl/FileTransferControlModal"
  ),
);
const AddEditFtcRulePage = lazy(() =>
  import(
    "../components/dashboard/Policy/SshPolicy/FileTransferControl/AddEditFtcRule"
  ),
);
const PasswordPolicy = lazy(() =>
  import("../components/dashboard/Policy/PasswordPolicy/PasswordPolicy"),
);
const PasswordPolicyAdd = lazy(() =>
  import("../components/dashboard/Policy/PasswordPolicy/PasswordPolicyAdd"),
);
const PasswordRotationPolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/PasswordRotationPolicy/PasswordRotationPolicy"
  ),
);
const MostActiveUser = lazy(() =>
  import("../components/dashboard/Reports/MostActiveUserDetail"),
);
const RecentApps = lazy(() => import("../components/dashboard/Reports/RecentApps"));
const RecentAssets = lazy(() => import("../components/dashboard/Reports/RecentAssets"));
const RecentWebApps = lazy(() =>
  import("../components/dashboard/Reports/RecentWebApps"),
);
const MyResources = lazy(() => import("../components/dashboard/Resources/MyResources"));
const ResourceDetailPage = lazy(() =>
  import("../components/dashboard/Resources/ResourceDetailPage")
);
const ResourceList = lazy(() =>
  import("../components/dashboard/Resources/ResourceList"),
);
const Resources = lazy(() => import("../components/dashboard/Resources/Resources"));
const AddRole = lazy(() => import("../components/dashboard/Roles/AddRole/AddRole"));
const MainAllocateRole = lazy(() =>
  import("../components/dashboard/Roles/AllocateRole/MainAllocateRole"),
);
const MainDeAllocateRole = lazy(() =>
  import("../components/dashboard/Roles/AllocateRole/MainDeAllocateRole"),
);
const EditRole = lazy(() => import("../components/dashboard/Roles/EditRole/EditRole"));
const RolesList = lazy(() => import("../components/dashboard/Roles/RolesList"));
const Sso = lazy(() => import("../components/dashboard/SSO/Sso"));
const License = lazy(() => import("../components/dashboard/Settings/License"));
const PasswordVaultConfig = lazy(() =>
  import("../components/dashboard/Settings/PasswordVaultConfig"),
);
const ProductSetting = lazy(() =>
  import("../components/dashboard/Settings/ProductSettings"),
);
const AddClientCredentials = lazy(() =>
  import("../components/dashboard/Settings/ScopedAPIKeys/AddClientCredentials"),
);
const ClientCredentialsList = lazy(() =>
  import("../components/dashboard/Settings/ScopedAPIKeys/ClientCredentialsList"),
);
const EditClientCredentials = lazy(() =>
  import("../components/dashboard/Settings/ScopedAPIKeys/EditClientCredentials"),
);
const ScopedAPIKeysList = lazy(() =>
  import("../components/dashboard/Settings/ScopedAPIKeys/ScopedAPIKeysList"),
);
const StorageConfig = lazy(() =>
  import("../components/dashboard/Settings/StorageConfiguration"),
);
const TicketSettings = lazy(() =>
  import("../components/dashboard/Settings/TicketSettings/TicketSettings"),
);
const AddSystemUser = lazy(() =>
  import("../components/dashboard/SystemUsers/AddSystemUser/AddSystemUser"),
);
const EditSystemUser = lazy(() =>
  import("../components/dashboard/SystemUsers/EditSystemUser/EditSystemUser"),
);
const MySystemUsers = lazy(() =>
  import("../components/dashboard/SystemUsers/MySystemUsers/MySystemUsers"),
);
const MyTickets = lazy(() => import("../components/dashboard/Ticketing/MyTickets"));
const TicketDetails = lazy(() =>
  import("../components/dashboard/Ticketing/TicketDetails"),
);
const EndUserConfigurations = lazy(() =>
  import("../components/dashboard/TwoFa/EndUserOptions/EndUserConfigurations"),
);
const GoogleAuthenticator = lazy(() =>
  import("../components/dashboard/TwoFa/GoogleAuthenticator/GoogleAuthenticator"),
);
const MFAList = lazy(() => import("../components/dashboard/TwoFa/MFAList/MFAList"));
const AddUsers = lazy(() => import("../components/dashboard/Users/AddUsers/AddUsers"));
const AllocateUserToGroup = lazy(() =>
  import("../components/dashboard/Users/AllocateUser/AllocateUserToGroups"),
);
const DeAllocateUserFromGroups = lazy(() =>
  import("../components/dashboard/Users/AllocateUser/DeAllocateUserFromGroups"),
);
const MainAllocateUser = lazy(() =>
  import("../components/dashboard/Users/AllocateUser/MainAllocateUser"),
);
const MainDeAllocateUser = lazy(() =>
  import("../components/dashboard/Users/AllocateUser/MainDeAllocateUser"),
);
const EditUser = lazy(() => import("../components/dashboard/Users/EditUser/EditUser"));
const UsersList = lazy(() => import("../components/dashboard/Users/UsersList"));
const SIEMConfiguration = lazy(() =>
  import("../components/dashboard/SIEM/SIEMConfiguration"),
);
const ActiveSessions = lazy(() =>
  import("../components/dashboard/Users/activesession"),
);
const HealthServiceStatus = lazy(() =>
  import("../components/dashboard/Settings/HealthService/HealthServiceStatus"),
);
const UsersProfile = lazy(() =>
  import("../components/dashboard/UsersProfile/UserProfile"),
);
const ThreatDetectionStatus = lazy(() =>
  import("../components/dashboard/Settings/ThreatDetection/ThreatDetectionStatus"),
);
const AddWebApp = lazy(() =>
  import("../components/dashboard/WebApps/AddWebApp/AddWebApp"),
);
const MainAllocateWebApp = lazy(() =>
  import("../components/dashboard/WebApps/AllocateWebApps/MainAllocateWebApps"),
);
const MainDeAllocateWebApp = lazy(() =>
  import("../components/dashboard/WebApps/AllocateWebApps/MainDeAllocateWebApps"),
);
const EditWebApp = lazy(() =>
  import("../components/dashboard/WebApps/EditWebApp/EditWebApp"),
);
const GoDaddy = lazy(() =>
  import("../components/dashboard/WebApps/CustomWebApp/GoDaddy/GoDaddyAdd"),
);
const GoDaddyEdit = lazy(() =>
  import("../components/dashboard/WebApps/CustomWebApp/GoDaddy/GoDaddyEdit"),
);
const SshPolicy = lazy(() =>
  import("../components/dashboard/Policy/SshPolicy/SshPolicyList/SshPolicy"),
);
const RdpPolicy = lazy(() =>
  import("../components/dashboard/Policy/RdpPolicy/RdpPolicyList/RdpPolicy"),
);
const WebAppPolicy = lazy(() =>
  import(
    "../components/dashboard/Policy/WebAppsPolicy/WebAppsPolicyList/WebAppsPolicy"
  ),
);
const TemplateConfiguration = lazy(() =>
  import("../components/dashboard/CustomTemplate/CustomTemplateDashboard"),
);
const CustomTemplate = lazy(() =>
  import("../components/dashboard/CustomTemplate/CustomEmailTemplate"),
);
const CustomNotifyTemplate = lazy(() =>
  import("../components/dashboard/CustomTemplate/CustomNotifyTemplate"),
);
const CertificateManager = lazy(() =>
  import("../components/dashboard/CertificateManager/CertificateManager"),
);
const CSRList = lazy(() =>
  import("../components/dashboard/CertificateManager/CSRList"),
);
const CARequestedCertificates = lazy(() =>
  import("../components/dashboard/CertificateManager/CARequestedCertificates"),
);
const CAProvider = lazy(() =>
  import("../components/dashboard/CertificateManager/CAProvider"),
);
const GenerateCSR = lazy(() =>
  import("../components/dashboard/CertificateManager/GenerateCSR/GenerateCSR"),
);
const CSRTemplates = lazy(() =>
  import("../components/dashboard/CertificateManager/CSRTemplates"),
);
const UploadCertificate = lazy(() =>
  import(
    "../components/dashboard/CertificateManager/UploadCertificate/UploadCertificate"
  ),
);
const DeviceUsers = lazy(() =>
  import("../components/dashboard/EndpointManagement/DeviceUsers"),
);
const DeviceInfo = lazy(() =>
  import("../components/dashboard/EndpointManagement/DeviceInfo"),
);

const ApplicationGroup = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationGroup/ApplicationGroup")
);
const CreateApplicationGroup = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationGroup/CreateApplicationGroup")
);
const ApplicationDefinition = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationDefinition/ApplicationDefinition")
);
const AddApplicationDefinition = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationDefinition/AddApplicationDefinition")
);
const DefaultApplicationsPage = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationDefinition/DefaultApplicationsPage")
);
const AddApplicationDefinitionToGroup = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationDefinition/AddApplicationDefinitionToGroup")
);
const EditApplicationDefinition = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationDefinition/EditApplicationDefinition")
);
const AssignPolicy = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationDefinition/AssignPolicy")
);
const ApplicationGroupDefinitions = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationGroup/ApplicationGroupDefinitions")
);
const EditApplicationGroup = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationGroup/EditApplicationGroup")
);
const AssignPolicyToGroup = lazy(() =>
  import("../components/dashboard/ApplicationManagement/ApplicationGroup/AssignPolicyToGroup")
);
const EPMDefaultPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMDefaultPolicy/EPMDefaultPolicy")
);
const EPMCustomPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/EPMCustomPolicy")
);
const CreateEPMCustomPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/CreateEPMCustomPolicy")
);
const EditEPMCustomPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/EditEPMCustomPolicy")
);
const EditDefaultPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMDefaultPolicy/EditDefaultPolicy")
);
const AssignApplicationGroupToPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/AssignApplicationGroupToPolicy")
);
const AssignApplicationDefinitionToPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/AssignApplicationDefinitionToPolicy")
);
const AssignDeviceToPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/AssignDeviceToPolicy")
);
const AssignDeviceGroupToPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/AssignDeviceGroupToPolicy")
);
const AssignUserToPolicy = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/AssignUserToPolicy")
);
const AssignPolicyToUser = lazy(() =>
  import("../components/dashboard/EPMPolicyManagement/EPMCustomPolicy/AssignPolicyToUser")
);

const EditApplicationCredentials = lazy(() =>
  import("../components/dashboard/ApplicationCredentials/EditApplicationCredentials/EditApplicationCredentials"));
const EpmAuditsReport = lazy(() =>
  import("../components/dashboard/Reports/EpmAuditsReport"),
);

const Users = lazy(() => import("../components/dashboard/EndpointManagement/Users"));

const Devices = lazy(() => import("../components/dashboard/EndpointManagement/Devices"));
const DeviceGroup = lazy(() => import("../components/dashboard/EndpointManagement/DeviceGroup"));
const DeviceGroupDevices = lazy(() => import("../components/dashboard/EndpointManagement/DeviceGroupDevices"));
const DeviceAssignPolicy = lazy(() => import("../components/dashboard/EndpointManagement/DeviceAssignPolicy"));
const DeviceGroupAssignPolicy = lazy(() =>
  import("../components/dashboard/EndpointManagement/DeviceGroupAssignPolicy")
);
const CreateDeviceGroup = lazy(() =>
  import("../components/dashboard/EndpointManagement/CreateDeviceGroup")
);
const EditDeviceGroup = lazy(() =>
  import("../components/dashboard/EndpointManagement/EditDeviceGroup")
);
const DownloadAgent = lazy(() => import("../components/dashboard/EndpointManagement/DownloadAgent"));
const ManagePlan = lazy(() => import("../components/dashboard/EndpointManagement/UpdatePlans/ManagePlan"));
const CreateUpdatePlan = lazy(() => import("../components/dashboard/EndpointManagement/UpdatePlans/CreateUpdatePlan"));
const PlanDetail = lazy(() => import("../components/dashboard/EndpointManagement/UpdatePlans/PlanDetail"));
const RingDevices = lazy(() => import("../components/dashboard/EndpointManagement/UpdatePlans/RingDevices"));
const ThreatsBlocked = lazy(() =>
  import("../components/dashboard/ThreatsBlocked/ThreatsBlocked"),
);
const AlertsSent = lazy(() =>
  import("../components/dashboard/AlertsSent/AlertsSent"),
);

const CustomProfileFields = lazy(() =>
  import("../components/dashboard/CustomProfileFields/customFieldsList"),
);
const AddCustomField = lazy(() =>
  import("../components/dashboard/CustomProfileFields/AddCustomFields/AddCustomField"),
);
const EditCustomField = lazy(() =>
  import("../components/dashboard/CustomProfileFields/EditCustomField/EditCustomField"),
);

const WorkFlowList = lazy(() =>
  import("../components/dashboard/Automations/WorkFlowList/Workflow/WorkFlow.js"),
);

const AutomationWorkFlow = lazy(() =>
  import("../components/dashboard/Automations/AutomationWorkFlow.js"),
);

const ExecutionHistory = lazy(() =>
  import("../components/dashboard/Automations/WorkFlowList/ExecutionHistory.js"),
);
const MspCustomers = lazy(() =>
  import("../components/dashboard/SuperAdmin/Customers/Customers"),
);
const MspAddCustomer = lazy(() =>
  import("../components/dashboard/SuperAdmin/Customers/AddCustomer/AddCustomer"),
);

const DashboardRoutes = () => {
  const capabilities = useSelector((state) => state.user?.user?.role?.capabilities || {});
  const isCapable = "is_admin_user" in capabilities ? capabilities : false;
  const isCapableEnduser = "is_end_user" in capabilities ? capabilities : false;
  const { isAuthenticated } = useSelector((state) => state.user);
  const roleName = useSelector((state) => state.user?.user?.role?.role_name);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const customerId = new URLSearchParams(location.search).get('customer_id');
    if (customerId && roleName !== "SuperAdmin") {
      localStorage.removeItem('managed_customer_id');
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, roleName]);

  const managedCustomerId = new URLSearchParams(location.search).get('customer_id') || '';

  return (
    <Routes>
      <Route
        path=""
        element={
          <Suspense fallback={<HomeSkeleton />}>
            <Home key={managedCustomerId} isAuditDashboard={false} />
          </Suspense>
        }
      />
      {isAuthenticated && <Route path="*" element={<Navigate to="/dashboard" />} />}
      {isCapable && isCapable.customer_management?.view_customers && (
        <Route
          path="customers/list"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MspCustomers
                addPath="/dashboard/customers/add"
                listPath="/dashboard/customers/list"
              />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.customer_management?.view_customers && (
        <Route
          path="customers/add"
          element={
            <Suspense fallback={<div />}>
              <MspAddCustomer listPath="/dashboard/customers/list" />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_users?.view && (
        <Route
          path="endpoint-management/devices/:device_id/users"
          element={
            <Suspense>
              <DeviceUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_info?.view && (
        <Route
          path="endpoint-management/devices/:device_id/info"
          element={
            <Suspense>
              <DeviceInfo />
            </Suspense>
          }
        />
      )}
      <Route
        path="two-fa"
        element={
          <Suspense>
            <GoogleAuthenticator />
          </Suspense>
        }
      />
      {((isCapableEnduser && isCapableEnduser.allow_password_change) ||
        (isCapable && isCapable.users.user.change_password)) && (
          <Route
            path="change-password"
            element={
              <Suspense>
                <ChangePassword />
              </Suspense>
            }
          />
        )}
      <Route
        path="users-profile"
        element={
          <Suspense>
            <UsersProfile />
          </Suspense>
        }
      />
      {isCapable &&
        isCapable.users &&
        isCapable.users.custom_profile_fields &&
        isCapable.users.custom_profile_fields.view && (
          <>
          <Route
              path="user/custom-fields"
              element={
                <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
                  <CustomProfileFields />
                </Suspense>
              }
            />
            <Route
            path="user/custom-fields/add"
            element={
              <Suspense fallback={<HomeSkeleton />}>
                <AddCustomField />
              </Suspense>
            }
          />
          <Route
            path="user/custom-fields/edit/:fieldId"
            element={
              <Suspense fallback={<HomeSkeleton />}>
                <EditCustomField />
              </Suspense>
            }
          />
        </>
      )}
      {isCapable && isCapable.miscellaneous.setting.network_analyzer_scan && (
        <Route
          path="scannedhistory"
          element={
            <Suspense>
              <ScannedHistory />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.miscellaneous.setting.network_analyzer_scan && (
        <Route
          path="scannedsystemusers"
          element={
            <Suspense>
              <ScannedSystemUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.LDAP.view && (
        <Route
          path="ldap"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <Configurations />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.LDAP.add && (
        <Route
          path="ldap/addnetworkconfiguration"
          element={
            <Suspense>
              <AddConfiguration />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.LDAP.edit && (
        <Route
          path="ldap/editnetworkconfiguration"
          element={
            <Suspense>
              <EditConfiguration />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.network_scan.view && (
        <Route
          path="discovery/network_configuration"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <NetworkConfiguration />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.siem.view && (
        <Route
          path="siem_configuration"
          element={
            <Suspense>
              <SIEMConfiguration />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.network_scan.view && (
        <Route
          path="discovery/network_configuration/add-networkconfiguration"
          element={
            <Suspense>
              <NetworkConfigurationOperations />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.cloud_discovery?.view && (
        <Route
          path="discovery/cloudconfiguration"
          element={<CloudConfiguration />}
        />
      )}
      {isCapable && isCapable.cloud_discovery?.add && (
        <Route
          path="discovery/cloudconfiguration/select-plateform/add-configuration"
          element={<SelectPlatform />}
        />
      )}
      {isCapable && isCapable.cloud_discovery?.add && (
        <Route
          path="discovery/cloudconfiguration/add-cloudconfiguration"
          element={<CloudDiscoveryOperations />}
        />
      )}
      {isCapable && isCapable.cloud_discovery?.edit && (
        <Route
          path="discovery/cloudconfiguration/edit-cloudconfiguration/:id"
          element={<CloudDiscoveryOperations />}
        />
      )}
      {isCapable && isCapable.cloud_discovery?.view_scan_results && (
        <Route
          path="discovery/cloudconfiguration/history/:config_id/results/:scan_id"
          element={<ScanResults />}
        />
      )}
      {isCapable && isCapable.cloud_discovery?.view_scan_history && (
        <Route
          path="discovery/cloudconfiguration/history/:config_id"
          element={<ScanHistory />}
        />
      )}
      {/* Hardcoded Credentials Scanning */}
      {isCapable && isCapable.credential_scanning?.view && (
        <>
          <Route path="discovery/hardcoded-credentials" element={<HardcodedCredentials />} />
          <Route path="discovery/hardcoded-credentials/findings" element={<CredFindings />} />
          <Route path="discovery/hardcoded-credentials/scan-history" element={<CredScanHistory />} />
          <Route path="discovery/hardcoded-credentials/scan-history/:scanId/details" element={<CredScanDetailPage />} />
          <Route path="discovery/hardcoded-credentials/configs" element={<CredScanConfigs />} />
          <Route path="discovery/hardcoded-credentials/configs/new" element={<CredCreateEditConfigPage />} />
          <Route path="discovery/hardcoded-credentials/configs/:configId/edit" element={<CredCreateEditConfigPage />} />
          <Route path="discovery/hardcoded-credentials/git-repos" element={<CredGitRepos />} />
          <Route path="discovery/hardcoded-credentials/git-repos/new" element={<CredGitConnectionFormPage />} />
          <Route path="discovery/hardcoded-credentials/git-repos/:repoId/edit" element={<CredGitConnectionFormPage />} />
        </>
      )}
      {isCapable && isCapable.network_scan.scan_networks && (
        <Route
          path="discovery/network_configuration/network_scan_results"
          element={
            <Suspense>
              <NetworkScanHistory />
            </Suspense>
          }
        />
      )}
      <Route
        path="tickets"
        element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <MyTickets />
          </Suspense>
        }
      />
      <Route
        path="tickets/:ticket_id"
        element={
          <Suspense>
            <TicketDetails />
          </Suspense>
        }
      />
      <Route
        path="endpoint-management/tickets"
        element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <MyTickets />
          </Suspense>
        }
      />
      <Route
        path="endpoint-management/tickets/:ticket_id"
        element={
          <Suspense>
            <TicketDetails />
          </Suspense>
        }
      />
      {isCapableEnduser && isCapableEnduser.view_allocated_apps && (
        <Route
          path="recent-apps"
          element={
            <Suspense>
              <RecentApps />
            </Suspense>
          }
        />
      )}
      {isCapableEnduser && isCapableEnduser.view_allocated_assets && (
        <Route
          path="recent-assets"
          element={
            <Suspense>
              <RecentAssets />
            </Suspense>
          }
        />
      )}
      {isCapableEnduser && isCapableEnduser.view_allocated_web_apps && (
        <Route
          path="recent-webapps"
          element={
            <Suspense>
              <RecentWebApps />
            </Suspense>
          }
        />
      )}
      {((isCapable && isCapable.MFA.enable_disable_mfa) ||
        (isCapableEnduser && isCapableEnduser.enable_disable_mfa)) && (
          <Route
            path="mfa/list"
            element={
              <Suspense fallback={<MFAListSkeleton showHeader={true} />}>
                <MFAList />
              </Suspense>
            }
          />
        )}
      {((isCapable && isCapable.MFA.enable_disable_mfa) ||
        (isCapableEnduser && isCapableEnduser.enable_disable_mfa)) && (
        <Route
          path="mfa/fido2"
          element={<Navigate to="/dashboard/mfa/list?section=fido2" replace />}
        />
      )}
      {isCapable && isCapable.MFA.configure_mfa && (
        <Route
          path="mfa/configurations"
          element={
            <Suspense>
              <EndUserConfigurations />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.SSO.enable_disable_sso && (
        <Route
          path="sso"
          element={
            <Suspense>
              <Sso />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.SSO.enable_disable_sso && (
        <Route
          path="autoAssign"
          element={
            <Suspense>
              <AutoAssign />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.miscellaneous.license.view_license && (
        <Route
          path="license"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <License />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.apps.add && (
        <Route
          path="resources/apps/add-apps/:app_type"
          element={
            <Suspense>
              <AddApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.apps.edit && (
        <Route
          path="resources/apps/edit-apps"
          element={
            <Suspense>
              <EditMyApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.assets.add && (
        <Route
          path="resources/assets/add-assets/:asset_type"
          element={
            <Suspense>
              <AddAsset />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.application_credentials?.add && (
        <Route
          path="resources/assets/add-assets/application-credentials/:credentialType?"
          element={
            <Suspense>
              <AddApplicationCredentials />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.assets.edit && (
        <Route
          path="resources/assets/edit-assets"
          element={
            <Suspense>
              <EditMyAsset />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.application_credentials?.edit && (
        <Route
          path="resources/assets/edit-application-credentials"
          element={
            <Suspense>
              <EditApplicationCredentials />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.edit && (
        <Route
          path="resources/web-apps/edit-webapps"
          element={
            <Suspense>
              <EditWebApp />
            </Suspense>
          }
        />
      )}
      {isCapable &&
        (isCapable.web_apps.add || isCapable.assets.add || isCapable.apps.add || isCapable.application_credentials?.add) && (
          <Route
            path="resources/add"
            element={
              <Suspense>
                <ResourceList />
              </Suspense>
            }
          />
        )}
      {isCapable && (
        <Route
          path="resources"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <Resources />
            </Suspense>
          }
        />
      )}
      <Route
        path="my-resources"
        element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <MyResources />
          </Suspense>
        }
      />
      <Route
        path="my-resources/:resourceType/:resourceId"
        element={
          <Suspense fallback={<DataTableSkeleton rowCount={5} columnCount={4} />}>
            <ResourceDetailPage />
          </Suspense>
        }
      />
      {isCapable && isCapable.web_apps.view && (
        <Route
          path="resources/web-apps/add"
          element={
            <Suspense>
              <AddWebApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.add && (
        <Route
          path="resources/web-apps/add/desktop-application"
          element={
            <Suspense>
              <AddWebApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.add && (
        <Route
          path="resources/web-apps/add/desktop-application/:desktop_application_name"
          element={
            <Suspense>
              <AddWebApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.add && (
        <Route
          path="resources/web-apps/add/:webapp_name"
          element={
            <Suspense>
              <AddWebApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.add && (
        <Route
          path="resources/web-apps/add/go-daddy"
          element={
            <Suspense>
              <GoDaddy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.edit && (
        <Route
          path="resources/web-apps/edit/go-daddy/:webapp_id"
          element={
            <Suspense>
              <GoDaddyEdit />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.user.view === true && (
        <Route
          path="viewusers/:asset_id"
          element={
            <Suspense>
              <DeAllocateAssetFromUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.assets.allocate_assets && (
        <Route
          path="allocateusers/:asset_id"
          element={
            <Suspense>
              <AllocateAssetToUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.assets.deallocate_assets && (
        <Route
          path="viewusers/:app_id"
          element={
            <Suspense>
              <DeAllocateAssetFromUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.apps.allocate_apps && (
        <Route
          path="allocateusers/:app_id"
          element={
            <Suspense>
              <AllocateAppToUser />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.roles.view === true && (
        <Route
          path="roles"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <RolesList />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.client_credentials.view === true && (
        <Route
          path="client-credentials"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <ClientCredentialsList />
            </Suspense>
          }
        />
      )}

      {isCapable && isCapable.client_credentials.view === true && (
        <Route
          path="scoped-api-keys"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <ScopedAPIKeysList />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.roles.edit === true && (
        <Route
          path="roles/editrole"
          element={
            <Suspense>
              <EditRole />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.roles.add === true && (
        <Route
          path="roles/addrole"
          element={
            <Suspense>
              <AddRole />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.client_credentials.add === true && (
        <Route
          path="client-credentials/addclient"
          element={
            <Suspense>
              <AddClientCredentials />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.client_credentials.edit === true && (
        <Route
          path="client-credentials/editclient"
          element={
            <Suspense>
              <EditClientCredentials />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.user.view && (
        <Route
          path="usersList"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <UsersList />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.user.view && (
        <Route
          path="users/usersList/adduser"
          element={
            <Suspense>
              <AddUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.user.view && (
        <Route
          path="users/usersList/edituser"
          element={
            <Suspense>
              <EditUser />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups.view && (
        <Route
          path="usersList/assign-groups/:user_id"
          element={
            <Suspense>
              <AllocateUserToGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && (isCapable.apps.view || isCapable.assets.view) && (
        <Route
          path="usersList/resource-allocate/:user_id"
          element={
            <Suspense>
              <MainAllocateUser />
            </Suspense>
          }
        />
      )}
      {isCapable && (isCapable.apps.view || isCapable.assets.view) && (
        <Route
          path="usersList/resource-deallocate/:user_id"
          element={
            <Suspense>
              <MainDeAllocateUser />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups.view && (
        <Route
          path="usersList/view-groups/:user_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <DeAllocateUserFromGroups />
            </Suspense>
          }
        />
      )}
      {isCapable && (
        <Route
          path="users/usersList/users-active-sessions/:user_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <ActiveSessions />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.system_user.view && (
        <Route
          path="mysystemusers"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MySystemUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.system_user.view && (
        <Route
          path="mysystemusers/addsystemusers"
          element={
            <Suspense>
              <AddSystemUser />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.system_user.view && (
        <Route
          path="mysystemusers/edit-systemuser/:system_user_id"
          element={
            <Suspense>
              <EditSystemUser />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups.view && (
        <Route
          path="groups"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <GroupsList />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups.view && (
        <Route
          path="groups/addgroup"
          element={
            <Suspense>
              <AddGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups.view && (
        <Route
          path="groups/editgroup"
          element={
            <Suspense>
              <EditGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups.view && (
        <Route
          path="groups/assign-users/:group_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <AssignUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups.view && (
        <Route
          path="groups/assigned-users/:group_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <AssignedUsers />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.assets.allocate_assets && (
        <Route
          path="resources/assets/main-allocate/:asset_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainAllocateAsset />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.assets.deallocate_assets && (
        <Route
          path="resources/assets/main-deallocate/:asset_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainDeAllocateAsset />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.application_credentials?.allocate_application_credentials && (
        <Route
          path="resources/application-credentials/main-allocate/:application_credential_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainAllocateApplicationCredentials />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.application_credentials?.deallocate_application_credentials && (
        <Route
          path="resources/application-credentials/main-deallocate/:application_credential_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainDeAllocateApplicationCredentials />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.allocate_web_apps && (
        <Route
          path="web-apps/main-allocate/:web_app_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainAllocateWebApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.web_apps.deallocate_web_apps && (
        <Route
          path="web-apps/main-deallocate/:web_app_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainDeAllocateWebApp />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.apps.allocate_apps && (
        <Route
          path="resources/apps/main-allocate/:app_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainAllocateApps />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.apps.deallocate_apps && (
        <Route
          path="resources/apps/main-deallocate/:app_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <MainDeAllocateApps />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.miscellaneous.setting.show_product_setting && (
        <Route
          path="productSettings"
          element={
            <Suspense>
              <ProductSetting />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.miscellaneous.setting.health_service && (
        <Route
          path="healthService"
          element={
            <Suspense>
              <HealthServiceStatus />
            </Suspense>
          }
        />
      )}
      {isCapable?.miscellaneous?.setting?.threat_detection && (
        <>
          <Route
            path="threat-detection"
            element={
              <Suspense fallback={<HomeSkeleton />}>
                <ThreatDetectionStatus />
              </Suspense>
            }
          />
          <Route
            path="threats-blocked"
            element={
              <Suspense fallback={<HomeSkeleton />}>
                <ThreatsBlocked />
              </Suspense>
            }
          />
          <Route
            path="alerts-sent"
            element={
              <Suspense fallback={<HomeSkeleton />}>
                <AlertsSent />
              </Suspense>
            }
          />
        </>
      )}
      {isCapable && isCapable.miscellaneous.setting.show_ticket_setting && (
        <Route
          path="ticket-settings"
          element={
            <Suspense>
              <TicketSettings />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.branding.edit_branding_setting && (
        <Route
          path="Branding"
          element={
            <Suspense>
              <Logo />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.branding.manage_email_provider && (
        <Route
          path="configure-smtp"
          element={
            <Suspense>
              <ConfigureSMTP />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.certificates.view && (
        <Route
          path="certificates"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CertificateManager />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.certificates.view && (
        <Route
          path="csrs"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CSRList />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.certificates.view && (
        <Route
          path="ca-providers"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CAProvider />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.certificates.view && (
        <Route
          path="ca-providers/:providerName"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CARequestedCertificates />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.certificates.generate_csr && (
        <Route
          path="certificates/generate-csr"
          element={
            <Suspense>
              <GenerateCSR />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.certificates.view && (
        <Route
          path="certificates/csr-templates"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CSRTemplates />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.certificates.add && (
        <Route
          path="certificates/upload"
          element={
            <Suspense>
              <UploadCertificate />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.report.active_usage_report && (
        <Route
          path="most-active-user-details/:username"
          element={
            <Suspense>
              <MostActiveUser />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.miscellaneous.setting.configure_external_storage && (
        <Route
          path="storageconfig"
          element={
            <Suspense>
              <StorageConfig />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.miscellaneous.setting.configure_vault_configurations && (
        <Route
          path="vaultconfig"
          element={
            <Suspense>
              <PasswordVaultConfig />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_database_policy.view && (
        <Route
          path="policies/database"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <DatabasePolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_database_policy.view && (
        <Route
          path="policies/database/add"
          element={
            <Suspense>
              <AddDatabasePolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_database_policy.view && (
        <Route
          path="policies/database/edit/:policy_id"
          element={
            <Suspense>
              <EditDatabasePolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_password_policy.view && (
        <Route
          path="policies/password/:password_policy_id"
          element={
            <Suspense>
              <PasswordPolicyAdd />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.roles.allocate_deallocate_resources && (
        <Route
          path="roles/resource-allocate/:role_id"
          element={
            <Suspense>
              <MainAllocateRole />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.users.roles.allocate_deallocate_resources && (
        <Route
          path="roles/resource-deallocate/:role_id"
          element={
            <Suspense>
              <MainDeAllocateRole />
            </Suspense>
          }
        />
      )}
      {isCapable &&
        isCapable.users &&
        isCapable.users.user &&
        isCapable.users.user.view && (
          <Route
            path="users/usersList"
            element={
              <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
                <UsersList />
              </Suspense>
            }
          />
        )}
      {isCapable &&
        ((isCapable.apps && isCapable.apps.view) ||
          (isCapable.assets && isCapable.assets.view)) && (
          <Route
            path="users/usersList/resource-allocate/:user_id"
            element={
              <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
                <MainAllocateUser />
              </Suspense>
            }
          />
        )}
      {isCapable &&
        ((isCapable.apps && isCapable.apps.view) ||
          (isCapable.assets && isCapable.assets.view)) && (
          <Route
            path="users/usersList/resource-deallocate/:user_id"
            element={
              <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
                <MainDeAllocateUser />
              </Suspense>
            }
          />
        )}
      {isCapable && isCapable.groups && isCapable.groups.view && (
        <Route
          path="users/usersList/view-groups/:user_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <DeAllocateUserFromGroups />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.groups && isCapable.groups.view && (
        <Route
          path="users/usersList/assign-groups/:user_id"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <AllocateUserToGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_password_policy.view && (
        <Route
          path="policies/password"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <PasswordPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_web_apps_policy.view && (
        <Route
          path="policies/webapps"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <WebAppPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_web_apps_policy.add && (
        <Route
          path="policies/webapps/add"
          element={
            <Suspense>
              <AddWebAppsPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_web_apps_policy.edit && (
        <Route
          path="policies/webapps/edit/:policy_id"
          element={
            <Suspense>
              <EditWebAppPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_ssh_policy.view && (
        <Route
          path="policies/ssh"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <SshPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_ssh_policy.add && (
        <Route
          path="policies/ssh/add"
          element={
            <Suspense>
              <AddSshPolicyPage />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_ssh_policy.edit && (
        <Route
          path="policies/ssh/edit/:policy_id"
          element={
            <Suspense>
              <EditSshPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_ssh_policy.edit && (
        <Route
          path="policies/ssh/file-transfer-control/:policy_id"
          element={
            <Suspense>
              <FileTransferControlPage />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_ssh_policy.add && (
        <Route
          path="policies/ssh/file-transfer-control/:policy_id/add-rule"
          element={
            <Suspense>
              <AddEditFtcRulePage />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_ssh_policy.edit && (
        <Route
          path="policies/ssh/file-transfer-control/:policy_id/edit-rule/:rule_id"
          element={
            <Suspense>
              <AddEditFtcRulePage />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_rdp_policy.view && (
        <Route
          path="policies/rdp"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <RdpPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_rdp_policy.add && (
        <Route
          path="policies/rdp/add"
          element={
            <Suspense>
              <AddRDPPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_rdp_policy.edit && (
        <Route
          path="policies/rdp/edit/:policy_id"
          element={
            <Suspense>
              <EditRDPPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_password_policy.view && (
        <Route
          path="policies/password/:password_policy_id"
          element={
            <Suspense>
              <PasswordPolicyAdd />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.policies.manage_password_rotation_policy.view && (
        <Route
          path="policies/password-rotation"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <PasswordRotationPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.account_discovery.discover_local_accounts && (
        <Route
          path="discovery/local-accounts/"
          element={
            <Suspense>
              <DiscoveryTabs />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.account_discovery.discover_local_accounts && (
        <Route
          path="discovery/local-accounts/status/:status_id"
          element={
            <Suspense>
              <DiscoveryTabs defaultTab={"Status"} />
            </Suspense>
          }
        />
      )}
      {isCapable &&
        (isCapable.branding.edit_email_template ||
          isCapable.branding.edit_notification_template) && (
          <Route
            path="custom-templates"
            element={
              <Suspense>
                <TemplateConfiguration />
              </Suspense>
            }
          />
        )}

      {isCapable && isCapable.branding.edit_email_template && (
        <Route
          path="custom-templates/email/:templateType"
          element={
            <Suspense>
              <CustomTemplate />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.branding.edit_notification_template && (
        <Route
          path="custom-templates/notification/:templateType"
          element={
            <Suspense>
              <CustomNotifyTemplate />
            </Suspense>
          }
        />
      )}
      {isCapable && (
        <Route
          path="epm"
          element={
            <Suspense fallback={<EPMDashboardSkeleton />}>
              <Home key={managedCustomerId} isEpmDashboard={true} />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.reports?.manage_epm_user_audit_action && (
        <Route
          path="epm/audits"
          element={
            <Suspense>
              <EpmAuditsReport />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_application_group?.view && (
        <Route
          path="application-management/application-group"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={6} columnCount={4} />}>
              <ApplicationGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_application_group?.add && (
        <Route
          path="application-management/application-group/create-application-group"
          element={
            <Suspense>
              <CreateApplicationGroup />
            </Suspense>
          }
        />
      )}

      {isCapable && isCapable.epm?.manage_epm_application_definition?.view && (
        <Route
          path="application-management/application-definition"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={6} columnCount={4} />}>
              <ApplicationDefinition />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_application_definition?.add && (
        <Route
          path="application-management/application-definition/add-application-definition"
          element={
            <Suspense>
              <AddApplicationDefinition />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_application_definition?.add && (
        <Route
          path="application-management/application-definition/default-applications"
          element={
            <Suspense>
              <DefaultApplicationsPage />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_application_definition?.edit && (
        <Route
          path="application-management/application-definition/:applicationDefinitionId/edit"
          element={
            <Suspense>
              <EditApplicationDefinition />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_app_def?.view && (
        <Route
          path="application-management/application-definition/:applicationDefinitionId/assign-policy"
          element={
            <Suspense>
              <AssignPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_users?.view && (
        <Route
          path="endpoint-management/users"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={6} columnCount={4} />}>
              <Users />
            </Suspense>
          }
        />
      )}

      {isCapable && isCapable.epm?.manage_epm_device_users?.view && (
        <Route
          path="endpoint-management/devices"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={6} columnCount={4} />}>
              <Devices />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_users?.view && (
        <Route
          path="endpoint-management/devices-group"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <DeviceGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_users?.view && (
        <Route
          path="endpoint-management/devices-group/:deviceGroupId/devices"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <DeviceGroupDevices view="members" />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_users?.view && (
        <Route
          path="endpoint-management/devices-group/:deviceGroupId/manage-devices"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <DeviceGroupDevices view="manage" />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_device?.view && (
        <Route
          path="endpoint-management/devices/:deviceId/assign-policy"
          element={
            <Suspense>
              <DeviceAssignPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_device_grp?.view && (
        <Route
          path="endpoint-management/devices-group/:deviceGroupId/assign-policy"
          element={
            <Suspense>
              <DeviceGroupAssignPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_users?.add && (
        <Route
          path="endpoint-management/devices-group/create-device-group"
          element={
            <Suspense>
              <CreateDeviceGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_device_users?.edit && (
        <Route
          path="endpoint-management/devices-group/:deviceGroupId/edit"
          element={
            <Suspense>
              <EditDeviceGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_agent?.add && (
        <Route
          path="endpoint-management/download-agent"
          element={
            <Suspense>
              <DownloadAgent />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_update_plans?.view && (
        <Route
          path="endpoint-management/manage-plan"
          element={
            <Suspense>
              <ManagePlan />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_update_plans?.add && (
        <Route
          path="endpoint-management/manage-plan/create"
          element={
            <Suspense>
              <CreateUpdatePlan />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_update_plans?.view && (
        <Route
          path="endpoint-management/manage-plan/:planId"
          element={
            <Suspense>
              <PlanDetail />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_update_plans?.view && (
        <Route
          path="endpoint-management/manage-plan/:planId/rings/:ringId/devices"
          element={
            <Suspense>
              <RingDevices />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy?.view && (
        <Route
          path="epm-policy-management/epm-default-policy"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <EPMDefaultPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy?.view && (
        <Route
          path="epm-policy-management/epm-custom-policy"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <EPMCustomPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy?.add && (
        <Route
          path="epm-policy-management/epm-custom-policy/create-custom-policy"
          element={
            <Suspense>
              <CreateEPMCustomPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy?.edit && (
        <Route
          path="epm-policy-management/epm-custom-policy/:policyId/edit"
          element={
            <Suspense>
              <EditEPMCustomPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy?.edit && (
        <Route
          path="epm-policy-management/epm-default-policy/:policyId/edit"
          element={
            <Suspense>
              <EditDefaultPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_app_grp?.view && (
        <Route
          path="epm-policy-management/epm-custom-policy/policy/:policyId/manage-application-groups"
          element={
            <Suspense>
              <AssignApplicationGroupToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_app_grp?.view && (
        <Route
          path="epm-policy-management/epm-default-policy/policy/:policyId/manage-application-groups"
          element={
            <Suspense>
              <AssignApplicationGroupToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_app_def?.view && (
        <Route
          path="epm-policy-management/epm-custom-policy/policy/:policyId/manage-application-definitions"
          element={
            <Suspense>
              <AssignApplicationDefinitionToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_app_def?.view && (
        <Route
          path="epm-policy-management/epm-default-policy/policy/:policyId/manage-application-definitions"
          element={
            <Suspense>
              <AssignApplicationDefinitionToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_device?.view && (
        <Route
          path="epm-policy-management/epm-custom-policy/policy/:policyId/manage-devices"
          element={
            <Suspense>
              <AssignDeviceToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_device?.view && (
        <Route
          path="epm-policy-management/epm-default-policy/policy/:policyId/manage-devices"
          element={
            <Suspense>
              <AssignDeviceToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_device_grp?.view && (
        <Route
          path="epm-policy-management/epm-custom-policy/policy/:policyId/manage-device-groups"
          element={
            <Suspense>
              <AssignDeviceGroupToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_device_grp?.view && (
        <Route
          path="epm-policy-management/epm-default-policy/policy/:policyId/manage-device-groups"
          element={
            <Suspense>
              <AssignDeviceGroupToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_user?.view && (
        <Route
          path="epm-policy-management/epm-custom-policy/policy/:policyId/manage-users"
          element={
            <Suspense>
              <AssignUserToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_user?.view && (
        <Route
          path="epm-policy-management/epm-default-policy/policy/:policyId/manage-users"
          element={
            <Suspense>
              <AssignUserToPolicy />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_user?.view && (
        <Route
          path="endpoint-management/devices/users/:userId/policies"
          element={
            <Suspense>
              <AssignPolicyToUser />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_application_association?.edit && (
        <Route
          path="application-management/application-group/:applicationGroupId/definitions"
          element={
            <Suspense>
              <ApplicationGroupDefinitions />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm.manage_epm_application_group.edit && (
        <Route
          path="application-management/application-group/:applicationGroupId/edit"
          element={
            <Suspense>
              <EditApplicationGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_policy_app_grp?.view && (
        <Route
          path="application-management/application-group/:applicationGroupId/assign-policy"
          element={
            <Suspense>
              <AssignPolicyToGroup />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.epm?.manage_epm_application_association?.edit && (
        <Route
          path="application-management/application-group/:applicationGroupId/add-application-definition"
          element={
            <Suspense>
              <AddApplicationDefinitionToGroup />
            </Suspense>
          }
        />
      )}
      {/* Workflow Automations Routes */}
      {isCapable && isCapable.automations?.workflows?.view && (
        <Route
          path="automations/workflows"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <WorkFlowList />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.automations?.workflows?.add && (
        <Route
          path="automations/workflows/create"
          element={
            <Suspense fallback={<HomeSkeleton />}>
              <AutomationWorkFlow />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.automations?.workflows?.edit && (
        <Route
          path="automations/workflows/edit"
          element={
            <Suspense fallback={<HomeSkeleton />}>
              <AutomationWorkFlow />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.automations?.execution_history?.view && (
        <Route
          path="automations/execution-history"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <ExecutionHistory />
            </Suspense>
          }
        />
      )}
    </Routes>
  );
};

export default DashboardRoutes;
